import { buildCapsules, capsuleTracks, type Capsule } from './clearance'
import type { GaitResult } from './gait'
import type { GroundResult } from './ground'
import type { Skeleton } from './kinematics'
import type { HumanoidModel } from './model'

/**
 * Die Umhüllung — welchen Raum ein Bauteil im Zyklus überstreicht.
 *
 * WOFÜR, WENN ES DIE PAARPRÜFUNG SCHON GIBT. Die Prüfung sagt, DASS zwei
 * Bauteile sich zu nahe kommen, und bei welcher Phase. Sie sagt nicht, WO im
 * Raum das passiert und wieviel Platz eine Baugruppe insgesamt braucht. Genau
 * das ist die Frage, die vor der Konstruktion steht: wie gross muss die
 * Verkleidung sein, wo darf kein Kabelkanal liegen, wie tief kommt der Fuss.
 *
 * ZWEI DARSTELLUNGEN, ZWEI FRAGEN:
 *
 *   MEHRFACHBILD  dasselbe Bauteil an achtzehn Phasen übereinander. Man sieht
 *                 die BEWEGUNG — wo es schnell durchläuft und wo es steht.
 *   HÜLLVOLUMEN   ein geschlossener Körper um alle Lagen. Man sieht den PLATZ —
 *                 aber nicht mehr, wann er gebraucht wird.
 *
 * DIE HÜLLE ZERFÄLLT IN ABSCHNITTE, und das ist der Kunstgriff, auf den es
 * ankommt. Eine einzige konvexe Hülle über den ganzen Zyklus umschliesst auch
 * den Raum zwischen zwei weit auseinanderliegenden Lagen — bei einem Bein, das
 * vor und zurück schwingt, ist das ein Keil, in dem das Bein nie war. Vier
 * Hüllen über je ein Viertel des Zyklus legen sich enger an; zusammen umfassen
 * sie dieselbe Bahn und behaupten weniger.
 *
 * ES WIRD BEWUSST ZU GROSS GERECHNET. Die Kapsel ist schon dicker als das
 * Bauteil, die konvexe Hülle füllt jede Delle auf, und die Abschnitte
 * überlappen um eine Phase. Bei einer Freigangsfrage ist Überschätzung das
 * richtige Vorzeichen: ein zu grosser Hüllkörper meldet einen Konflikt zu früh,
 * ein zu kleiner meldet ihn gar nicht.
 */

/* -------------------------------------------------------------------------
   Die Richtungen
   ------------------------------------------------------------------------- */

export interface Icosphere {
  /** Einheitsvektoren, flach: x, y, z je Richtung. */
  dirs: Float32Array
  /** Dreiecke über denselben Indizes. */
  faces: Uint16Array
  count: number
}

let cached: Icosphere | null = null

/**
 * Gleichmässig verteilte Richtungen auf der Kugel — zweimal unterteiltes
 * Ikosaeder, 162 Stück.
 *
 * WARUM NICHT KUGELKOORDINATEN. Ein Raster aus Längen- und Breitengraden häuft
 * die Punkte an den Polen und dünnt sie am Äquator aus; die Hülle wäre oben
 * genau und in der Mitte grob. Das unterteilte Ikosaeder verteilt sie
 * gleichmässig — und liefert die Dreiecke gleich mit, so dass dieselbe Liste
 * die Stützrichtungen UND die Topologie des Hüllkörpers ist.
 */
export function icoDirections(): Icosphere {
  if (cached) return cached

  const t = (1 + Math.sqrt(5)) / 2
  const v: number[][] = [
    [-1, t, 0], [1, t, 0], [-1, -t, 0], [1, -t, 0],
    [0, -1, t], [0, 1, t], [0, -1, -t], [0, 1, -t],
    [t, 0, -1], [t, 0, 1], [-t, 0, -1], [-t, 0, 1],
  ].map(normalise)

  let f: [number, number, number][] = [
    [0, 11, 5], [0, 5, 1], [0, 1, 7], [0, 7, 10], [0, 10, 11],
    [1, 5, 9], [5, 11, 4], [11, 10, 2], [10, 7, 6], [7, 1, 8],
    [3, 9, 4], [3, 4, 2], [3, 2, 6], [3, 6, 8], [3, 8, 9],
    [4, 9, 5], [2, 4, 11], [6, 2, 10], [8, 6, 7], [9, 8, 1],
  ]

  for (let step = 0; step < 2; step++) {
    const mid = new Map<string, number>()
    const next: [number, number, number][] = []
    const between = (a: number, b: number): number => {
      const key = a < b ? `${a}_${b}` : `${b}_${a}`
      let index = mid.get(key)
      if (index === undefined) {
        index = v.length
        mid.set(key, index)
        v.push(normalise([v[a][0] + v[b][0], v[a][1] + v[b][1], v[a][2] + v[b][2]]))
      }
      return index
    }
    for (const [a, b, c] of f) {
      const ab = between(a, b)
      const bc = between(b, c)
      const ca = between(c, a)
      next.push([a, ab, ca], [b, bc, ab], [c, ca, bc], [ab, bc, ca])
    }
    f = next
  }

  const dirs = new Float32Array(v.length * 3)
  for (let i = 0; i < v.length; i++) {
    dirs[i * 3] = v[i][0]
    dirs[i * 3 + 1] = v[i][1]
    dirs[i * 3 + 2] = v[i][2]
  }
  const faces = new Uint16Array(f.length * 3)
  for (let i = 0; i < f.length; i++) {
    faces[i * 3] = f[i][0]
    faces[i * 3 + 1] = f[i][1]
    faces[i * 3 + 2] = f[i][2]
  }

  cached = { dirs, faces, count: v.length }
  return cached
}

const normalise = (p: number[]): number[] => {
  const len = Math.hypot(p[0], p[1], p[2]) || 1
  return [p[0] / len, p[1] / len, p[2] / len]
}

/* -------------------------------------------------------------------------
   Die Punktwolke einer Kapsel
   ------------------------------------------------------------------------- */

/** Punkte je Endkreis. Acht sind genug: die Hülle ist ohnehin konvex. */
export const RING = 8

/** Wieviele Punkte eine Phase beisteuert — zwei Kreise und zwei Kappenspitzen. */
export const PER_PHASE = RING * 2 + 2

/**
 * Legt die Kapseloberfläche einer Bahn als Punkte ab.
 *
 * Je Phase zwei Ringe um die Endpunkte plus die beiden Kappenspitzen. Nur die
 * Mittelstrecke zu nehmen wäre einfacher und falsch: die Hülle darüber wäre um
 * den Radius zu klein, und zwar genau an den Stellen, an denen ein Bauteil an
 * ein anderes stösst.
 */
export function capsuleCloud(track: Float64Array, radius: number): Float32Array {
  const phases = track.length / 6
  const out = new Float32Array(phases * PER_PHASE * 3)
  let w = 0

  for (let i = 0; i < phases; i++) {
    const o = i * 6
    let ax = track[o + 3] - track[o]
    let ay = track[o + 4] - track[o + 1]
    let az = track[o + 5] - track[o + 2]
    const len = Math.hypot(ax, ay, az)
    if (len < 1e-9) {
      ax = 0
      ay = 1
      az = 0
    } else {
      ax /= len
      ay /= len
      az /= len
    }

    /* Zwei Querrichtungen zur Achse. Die erste steht senkrecht auf ihr, solange
       die Achse nicht selbst senkrecht steht — dann wird ausgewichen. */
    let n1x = -ay
    let n1y = ax
    let n1z = 0
    let n1 = Math.hypot(n1x, n1y, n1z)
    if (n1 < 1e-6) {
      n1x = 1
      n1y = 0
      n1z = 0
      n1 = 1
    }
    n1x /= n1
    n1y /= n1
    n1z /= n1
    const n2x = ay * n1z - az * n1y
    const n2y = az * n1x - ax * n1z
    const n2z = ax * n1y - ay * n1x

    for (let end = 0; end < 2; end++) {
      const bx = track[o + (end ? 3 : 0)]
      const by = track[o + (end ? 4 : 1)]
      const bz = track[o + (end ? 5 : 2)]
      for (let a = 0; a < RING; a++) {
        const w2 = (a / RING) * Math.PI * 2
        const ca = Math.cos(w2) * radius
        const sa = Math.sin(w2) * radius
        out[w++] = bx + n1x * ca + n2x * sa
        out[w++] = by + n1y * ca + n2y * sa
        out[w++] = bz + n1z * ca + n2z * sa
      }
      /* Die Kappenspitze — der Punkt, der über das Ende hinaussteht. */
      const s = end ? radius : -radius
      out[w++] = bx + ax * s
      out[w++] = by + ay * s
      out[w++] = bz + az * s
    }
  }

  return out
}

/* -------------------------------------------------------------------------
   Die Hülle
   ------------------------------------------------------------------------- */

/**
 * Der Stützkörper einer Punktwolke: je Richtung der äusserste Punkt.
 *
 * KEINE ECHTE KONVEXE HÜLLE, und das ist Absicht. Ein Quickhull über 1500
 * Punkte liefert ein Netz mit unvorhersehbarer Topologie — jeder Körper hätte
 * eine andere, und die Dreiecke müssten mitgeliefert werden. Der Stützkörper
 * hat für jede Wolke DIESELBE Topologie, nämlich die der Richtungskugel. Er
 * umschliesst die konvexe Hülle, liegt also immer aussen. Bei einer
 * Freigangsfrage ist das das richtige Vorzeichen.
 */
export function supportBody(cloud: Float32Array, from: number, to: number): Float32Array {
  const { dirs, count } = icoDirections()
  const out = new Float32Array(count * 3)

  for (let d = 0; d < count; d++) {
    const dx = dirs[d * 3]
    const dy = dirs[d * 3 + 1]
    const dz = dirs[d * 3 + 2]
    let best = -Infinity
    let bi = from
    for (let i = from; i < to; i++) {
      const q = cloud[i * 3] * dx + cloud[i * 3 + 1] * dy + cloud[i * 3 + 2] * dz
      if (q > best) {
        best = q
        bi = i
      }
    }
    out[d * 3] = cloud[bi * 3]
    out[d * 3 + 1] = cloud[bi * 3 + 1]
    out[d * 3 + 2] = cloud[bi * 3 + 2]
  }

  return out
}

/* -------------------------------------------------------------------------
   Was die Seite bekommt
   ------------------------------------------------------------------------- */

export interface BodyEnvelope {
  key: string
  label: string
  group: Capsule['group']
  side?: Capsule['side']
  radius: number
  /**
   * Tiefster Punkt über den Zyklus, in Metern über dem Boden.
   *
   * Die Unterkante der Kapsel und nicht die Achse: was den Boden berührt, ist
   * die Hülle. Ein negativer Wert heisst, dass das Bauteil im Zyklus unter die
   * Standebene taucht — beim Fuss richtig, bei allem anderen ein Befund.
   */
  lowest: number
  /** Die Kapselenden an den Phasen des Mehrfachbildes, sechs Zahlen je Phase. */
  ghosts: Float64Array
  /** Ein Stützkörper je Abschnitt, mit der Topologie aus `icoDirections`. */
  sections: Float32Array[]
}

export interface Envelope {
  bodies: BodyEnvelope[]
  /** Die Dreiecke, für alle Hüllkörper dieselben. */
  faces: Uint16Array
  /** Wieviele Phasen das Mehrfachbild führt. */
  ghostPhases: number
  /** In wieviele Abschnitte der Zyklus für die Hülle zerfällt. */
  sections: number
}

export interface EnvelopeOptions {
  /** Phasen für das Mehrfachbild. */
  ghostPhases?: number
  /** Phasen für die Hülle. */
  samples?: number
  /** Abschnitte des Zyklus. */
  sections?: number
  /** Nur diese Bauteile; leer heisst alle. */
  keys?: Iterable<string>
}

/**
 * Achtzehn Lagen für das Mehrfachbild.
 *
 * Weniger, und der Bogen zerfällt in einzelne Körper; mehr, und sie überdecken
 * einander so weit, dass die Bewegung nicht mehr abzulesen ist — man sieht dann
 * dasselbe wie beim Hüllvolumen, nur teurer gezeichnet.
 */
export const GHOST_PHASES = 18

/** Phasen für die Hülle. Sie zeichnet nicht, sie umschliesst nur. */
export const HULL_SAMPLES = 96

/** Vier Abschnitte — siehe oben, das ist der Kunstgriff. */
export const HULL_SECTIONS = 4

export function computeEnvelope(
  model: HumanoidModel,
  skeleton: Skeleton,
  gait: GaitResult,
  ground?: GroundResult,
  options: EnvelopeOptions = {},
): Envelope {
  const ghostPhases = options.ghostPhases ?? GHOST_PHASES
  const samples = options.samples ?? HULL_SAMPLES
  const sections = Math.max(1, options.sections ?? HULL_SECTIONS)
  const wanted = options.keys ? new Set(options.keys) : null

  const all = buildCapsules(skeleton, model.scale)
  const capsules = wanted ? all.filter((c) => wanted.has(c.key)) : all
  if (capsules.length === 0) {
    return { bodies: [], faces: icoDirections().faces, ghostPhases, sections }
  }

  const hullTracks = capsuleTracks(skeleton, capsules, gait, ground, samples)
  const ghostTracks = capsuleTracks(skeleton, capsules, gait, ground, ghostPhases)

  const bodies = capsules.map((capsule, k): BodyEnvelope => {
    const track = hullTracks[k]
    const cloud = capsuleCloud(track, capsule.radius)

    /* Der tiefste Punkt kommt aus der HÜLLABTASTUNG und nicht aus dem
       Mehrfachbild: achtzehn Phasen treffen den Umkehrpunkt nur zufällig. */
    let lowest = Infinity
    for (let i = 0; i < samples; i++) {
      const o = i * 6
      lowest = Math.min(lowest, track[o + 1] - capsule.radius, track[o + 4] - capsule.radius)
    }

    /*
      Die Abschnitte überlappen um eine Phase.

      Ohne die Überlappung klafft zwischen zwei Hüllkörpern eine Lücke von genau
      einem Abtastschritt — und ein Bauteil, das gerade dort am weitesten
      aussteht, läge in keiner der beiden Hüllen. Der Preis ist ein wenig mehr
      Volumen, und das ist die richtige Richtung.
    */
    const perSection = Math.max(1, Math.floor(samples / sections)) * PER_PHASE
    const total = samples * PER_PHASE
    const out: Float32Array[] = []
    for (let s = 0; s < sections; s++) {
      const from = s * perSection
      const to = Math.min(total, (s + 1) * perSection + PER_PHASE)
      if (to - from < PER_PHASE) continue
      out.push(supportBody(cloud, from, to))
    }

    return {
      key: capsule.key,
      label: capsule.label,
      group: capsule.group,
      side: capsule.side,
      radius: capsule.radius,
      lowest,
      ghosts: ghostTracks[k],
      sections: out,
    }
  })

  return { bodies, faces: icoDirections().faces, ghostPhases, sections }
}

/**
 * Die Bauteilliste ohne jede Rechnung — für die Auswahltabelle.
 *
 * Die Seite braucht die Namen, bevor irgendetwas gerechnet ist: sie zeigt die
 * Tabelle, und erst wer eine Darstellung einschaltet, bezahlt die Rechnung.
 */
export function envelopeBodies(model: HumanoidModel, skeleton: Skeleton): Capsule[] {
  return buildCapsules(skeleton, model.scale)
}
